import * as monacoEditor from 'monaco-editor';
import { useCallback, useEffect, useState } from 'react';

import { invertRangesInDocumentForDiffs } from '../../explorerPage/helpers/monacoHelpers';

import {
  HIDDEN_SECTION_HEIGHT_IN_LINES,
  getRangesToShow,
} from './diffEditorHelpers';
import { RangeExpandType } from './HiddenSectionIndicators';

export const useHideUnchangedLines = ({
  diffEditor,
  linesToShow,
}: {
  diffEditor: monacoEditor.editor.IStandaloneDiffEditor;
  // If omitted, this function shows lines changed in the diff editor between base and modified.
  // If included, this function shows these lines only and only on the modified side.
  // This is useful for the lint & custom check editor, which only show the modified side.
  linesToShow?: Array<number>;
}) => {
  const [hiddenRanges, setHiddenRanges] = useState<{
    modified: monacoEditor.Range[];
    original: monacoEditor.Range[];
  }>();

  const [expandedRanges, setExpandedRanges] = useState<{
    modified: monacoEditor.Range[];
    original: monacoEditor.Range[];
  }>();
  const [modifiedDecorations, setModifiedDecorations] =
    useState<monacoEditor.editor.IEditorDecorationsCollection>();
  const [originalDecorations, setOriginalDecorations] =
    useState<monacoEditor.editor.IEditorDecorationsCollection>();

  useEffect(() => {
    const setCollapsedAreas = () => {
      if (!diffEditor) return;
      const modifiedModel = diffEditor.getModifiedEditor().getModel();
      const originalModel = diffEditor.getOriginalEditor().getModel();
      if (modifiedModel?.getLineCount() === 1)
        // the model line count will be 1 before the modal has been populated
        return;

      const lineChanges: monacoEditor.editor.ILineChange[] | null = linesToShow
        ? linesToShow.map((lineNumber) => ({
            // the original line numbers don't matter, we don't show the original
            // side of the diff view in the places this component is used
            originalStartLineNumber: 0,
            originalEndLineNumber: 0,
            modifiedStartLineNumber: lineNumber,
            modifiedEndLineNumber: lineNumber,
            charChanges: undefined,
          }))
        : diffEditor.getLineChanges();
      if (!lineChanges || !modifiedModel || !originalModel) return;

      const modifiedRangesToShow = getRangesToShow({
        lineChanges,
        diffEditorSide: 'modified',
        model: modifiedModel,
        accountForHiddenSectionIndicator: true,
      });
      const originalRangesToShow = getRangesToShow({
        lineChanges,
        diffEditorSide: 'original',
        model: originalModel,
        accountForHiddenSectionIndicator: true,
      });

      const modifiedRangesToHide = invertRangesInDocumentForDiffs(
        modifiedModel,
        modifiedRangesToShow,
      ).filter(
        (range) =>
          // if the hidden range is < 3 lines, don't hide it
          range.endLineNumber - range.startLineNumber > 3,
      );
      const originalRangesToHide = invertRangesInDocumentForDiffs(
        originalModel,
        originalRangesToShow,
      ).filter(
        (range) =>
          // if the hidden range is < 3 lines, don't hide it
          range.endLineNumber - range.startLineNumber > 3,
      );

      setHiddenRanges({
        modified: modifiedRangesToHide,
        original: originalRangesToHide,
      });
    };
    const disposable = diffEditor.onDidUpdateDiff(() => {
      setCollapsedAreas();
    });

    setCollapsedAreas();

    return () => disposable.dispose();
  }, [diffEditor, linesToShow]);

  useEffect(() => {
    if (hiddenRanges?.modified && hiddenRanges.original) {
      diffEditor.getModifiedEditor().setHiddenAreas(hiddenRanges.modified);
      diffEditor.getOriginalEditor().setHiddenAreas(hiddenRanges.original);
    }
  }, [diffEditor, hiddenRanges]);

  const getNewExpandedAndHiddenRanges = useCallback(
    ({
      expandType,
      editorType,
      rangeIndex,
    }: {
      expandType: RangeExpandType;
      editorType: 'modified' | 'original';
      rangeIndex: number;
    }) => {
      const ogRange =
        editorType === 'modified'
          ? hiddenRanges?.modified[rangeIndex]
          : hiddenRanges?.original[rangeIndex];
      const filteredHiddenRanges = (
        editorType === 'modified'
          ? hiddenRanges?.modified
          : hiddenRanges?.original
      )?.filter((_, index) => index !== rangeIndex);
      if (!ogRange) return {};
      switch (expandType) {
        case 'top':
          return {
            rangeToExpand: new monacoEditor.Range(
              ogRange.startLineNumber,
              0,
              ogRange.startLineNumber + 9,
              0,
            ),
            newHiddenRanges: filteredHiddenRanges?.concat(
              new monacoEditor.Range(
                ogRange.startLineNumber + 10,
                0,
                ogRange.endLineNumber,
                0,
              ),
            ),
          };

        case 'bottom':
          return {
            rangeToExpand: new monacoEditor.Range(
              ogRange.endLineNumber - 9,
              0,
              ogRange.endLineNumber,
              0,
            ),
            newHiddenRanges: filteredHiddenRanges?.concat(
              new monacoEditor.Range(
                ogRange.startLineNumber,
                0,
                ogRange.endLineNumber - 10,
                0,
              ),
            ),
          };

        case 'full':
          return {
            rangeToExpand: ogRange,
            newHiddenRanges: filteredHiddenRanges,
          };
        default:
          throw new Error(
            'only `top`, `bottom` and `full` are valid expandTypes',
          );
      }
    },
    [hiddenRanges],
  );

  const handleExpandRange = useCallback(
    ({
      expandType,
      rangeIndex,
    }: {
      expandType: RangeExpandType;
      rangeIndex: number;
    }) => {
      const {
        rangeToExpand: modifiedRangeToExpand,
        newHiddenRanges: newModifiedHiddenRanges,
      } = getNewExpandedAndHiddenRanges({
        expandType,
        editorType: 'modified',
        rangeIndex,
      });

      const {
        rangeToExpand: originalRangeToExpand,
        newHiddenRanges: newOriginalHiddenRanges,
      } = getNewExpandedAndHiddenRanges({
        expandType,
        editorType: 'original',
        rangeIndex,
      });

      setExpandedRanges((prevExpandedRanges) => {
        const newModifiedExpandedRanges = (
          prevExpandedRanges?.modified ?? []
        ).concat(modifiedRangeToExpand ?? []);
        const newOriginalExpandedRanges = (
          prevExpandedRanges?.original ?? []
        ).concat(originalRangeToExpand ?? []);
        if (newModifiedExpandedRanges && newOriginalExpandedRanges) {
          return {
            modified: newModifiedExpandedRanges,
            original: newOriginalExpandedRanges,
          };
        }
        return prevExpandedRanges;
      });

      setHiddenRanges((prev) => {
        return {
          modified: newModifiedHiddenRanges ?? prev?.modified ?? [],
          original: newOriginalHiddenRanges ?? prev?.original ?? [],
        };
      });
    },
    [getNewExpandedAndHiddenRanges],
  );

  // remove highlighting when the diff models change
  useEffect(() => {
    const disposable = diffEditor.onDidUpdateDiff(() => {
      modifiedDecorations?.clear();
      originalDecorations?.clear();
    });
    return () => disposable.dispose();
  }, [diffEditor, modifiedDecorations, originalDecorations]);

  useEffect(() => {
    setModifiedDecorations((prevDecorations) =>
      expandedRanges
        ? diffEditor.getModifiedEditor().createDecorationsCollection(
            expandedRanges.modified.map((range) => {
              const rangeToHighlight = new monacoEditor.Range(
                // we need to subtract from the start to account for the
                // lines that need to be overlayed by the indicators
                range.startLineNumber - HIDDEN_SECTION_HEIGHT_IN_LINES,
                0,
                range.endLineNumber,
                0,
              );

              return {
                range: rangeToHighlight,
                options: {
                  isWholeLine: true,
                  className: 'xxx-monaco-expanded-lines',
                },
              };
            }),
          )
        : prevDecorations,
    );

    setOriginalDecorations((prevDecorations) =>
      expandedRanges
        ? diffEditor.getOriginalEditor().createDecorationsCollection(
            expandedRanges.original.map((range) => {
              const rangeToHighlight = new monacoEditor.Range(
                // we need to subtract from the start to account for the
                // lines that need to be overlayed by the indicators
                range.startLineNumber - HIDDEN_SECTION_HEIGHT_IN_LINES,
                0,
                range.endLineNumber,
                0,
              );
              return {
                range: rangeToHighlight,
                options: {
                  isWholeLine: true,
                  className: 'xxx-monaco-expanded-lines',
                },
              };
            }),
          )
        : prevDecorations,
    );
  }, [diffEditor, expandedRanges]);

  return {
    modifiedHiddenRanges: hiddenRanges?.modified,
    originalHiddenRanges: hiddenRanges?.original,
    handleExpandRange,
  };
};
